import { UIElement } from '../ui-element';
import { UIIcon } from '../icon/ui-icon';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type {
    UIIconColor,
    UIIconGlyphRedesign,
    UIIconSize,
} from '../icon/ui-icon.types';
import type { MaybeHTMLElement, NullOr } from '../../global/utils.type';
import { UIIconColors } from '../icon/ui-icon.helpers';
import styles from './ui-icon-progress.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @classdesc Represents a class for <code>ui-icon-progress</code> element.
 * @alias UIIconProgress
 * @element ui-icon-progress
 * @property {number} [total] {@attr total} Total icons to show.
 * @property {number} [fillPercentage] {@attr fill-percentage}
 * Fills percentage of all icons with fill-color.
 * @property {UIIconGlyph} glyph {@attr glyph} Represents glyph (symbol) icon
 * @property {("small" | "medium" | "large")} [size] {@attr size} Size of the icon
 *  {@desc small}
 *  {@desc medium}
 *  {@desc large}
 * @property {UIIconColor} [color="bark-30"] {@attr color} Color to be set on icon.
 * @property {UIIconColor} [fillColor="orange"] {@attr fill-color} Color to be set on filled icon.
 * @example
 * <ui-icon-progress total="10" fill-percentage="60" glyph="face-smile"></ui-icon-progress>
 */
@Component({ tag: 'ui-icon-progress', styles })
export class UIIconProgress extends UIElement {
    @Attribute(Number) total: number = 0;
    @Attribute(Number) fillPercentage: number = 0;
    @Attribute(String) glyph?: UIIconGlyphRedesign;
    @Attribute(String) size?: UIIconSize;
    @Attribute(String) color: UIIconColor = UIIconColors.BARK_30;
    @Attribute(String) fillColor: UIIconColor = UIIconColors.ORANGE;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'total',
            'fill-percentage',
            'glyph',
            'size',
            'color',
            'fill-color',
        ];
    }

    /**
     * @type {number}
     */
    get fillRatio(): number {
        return (this.fillPercentage * this.total) / 100;
    }

    /**
     * @type {number}
     */
    get fillRatioRounded(): number {
        return Math.round(this.fillRatio);
    }

    /**
     * Replaces elements.
     * @param {Array<ElementConfig | Node>} elements
     * @returns {HTMLElement}
     */
    replaceElements(elements: Array<MaybeHTMLElement>): HTMLElement {
        /* 'while' only executes when firstChild is non-null as well */
        while (this.lastChild) {
            this.removeChild(this.lastChild);
        }
        return this.insertElements(
            elements.filter((el): el is HTMLElement => el !== null)
        ) as HTMLElement;
    }

    /**
     * Returns icon color based on fill ratio.
     * @param {number} current
     * @returns {string}
     */
    getColor(current: number): UIIconColor {
        const fillRatio = this.fillRatioRounded;
        return current <= fillRatio ? this.fillColor : this.color;
    }

    /**
     * Creates new icon component.
     * @param {string?} color
     * @returns {UIIcon}
     */
    getIcon(color?: UIIconColor): HTMLElement {
        return this.createElement({
            tagName: 'ui-icon',
            attributes: {
                glyph: this.glyph,
                size: this.size,
                color: color || this.color,
            },
        });
    }

    /**
     * Adds half icon to progress when fillRatio is rounded higher.
     * @param {Array<HTMLElement>} icons
     */
    setHalfIcon(icons: Array<HTMLElement>): void {
        if (this.fillRatioRounded > this.fillRatio) {
            const halfIconIndex = this.fillRatioRounded - 1;
            const icon = icons.splice(halfIconIndex, 1)[0];
            icon.classList.add('ui-icon-progress__half-icon');

            const wrapper = this.createElement({
                tagName: 'div',
                classList: { 'ui-icon-progress__wrapper-half-icon': true },
                children: [this.getIcon(), icon],
            });
            icons.splice(halfIconIndex, 0, wrapper);
        }
    }

    /**
     * Constructs component
     */
    renderIconProgress(): void {
        const icons = new Array(this.total).fill(null).map((_, i) => {
            const color = this.getColor(i + 1);
            return this.getIcon(color);
        });

        this.setHalfIcon(icons);
        this.replaceElements(icons);
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'total':
            case 'fill-percentage':
            case 'color':
            case 'fill-color':
                this.renderIconProgress();
                this.setAccessibilityAttrs();
                break;
            case 'size':
                this.querySelectorAll<UIIcon>('ui-icon').forEach((icon) => {
                    if (this.size) {
                        icon.size = this.size;
                    }
                });
                break;
            case 'glyph':
                this.querySelectorAll<UIIcon>('ui-icon').forEach((icon) => {
                    if (this.glyph) {
                        icon.glyph = this.glyph;
                    }
                });
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @private
     */
    private setAccessibilityAttrs(): void {
        this.setAttribute('role', 'meter');
        if (this.total > 0) {
            this.setAttribute('aria-valuemin', '0');
            this.setAttribute('aria-valuemax', String(this.total));
            this.setAttribute('aria-valuenow', this.fillRatio.toFixed(1));
            this.setAttribute('aria-valuetext', this.fillRatio.toFixed(1));
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.renderIconProgress();
        this.setAccessibilityAttrs();
    }
}
